/**
 * Duplicate Review — side-by-side identity match review (Step 3, Contract #3).
 * Only definitive identifiers merge automatically; everything here needs a human decision.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Users, GitMerge, ArrowLeftRight, History, RotateCcw } from 'lucide-react';

const BAND_STYLES = {
  definitive: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  very_high: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  high: 'bg-sky-500/15 text-sky-600 border-sky-500/30',
  possible: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  candidate: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  weak: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
};

const SIGNAL_LABELS = {
  exact_mid: 'Same processor MID',
  exact_ein: 'Same EIN',
  legal_name_and_address: 'Legal name + exact address',
  dba_and_address: 'DBA + exact address',
  domain_match: 'Same web domain',
  business_name_only: 'Business name only — never auto-merged',
  exact_verified_email: 'Same verified email',
  email_and_name_match: 'Same email and matching name',
  email_match_unverified: 'Same unverified email — different name',
  lead_application_repair: 'Lead and application for the same sale',
  exact_normalized_phone: 'Same phone number',
  name_and_business: 'Same name at the same business',
  owner_name_similarity: 'Similar person name',
  same_source_record: 'Same source record',
  manual: 'Manual merge',
};

const addressLine = (a) =>
  !a ? '—' : [a.street, a.city, a.state, a.zip].filter(Boolean).join(', ') || '—';

function IdentityCard({ entity, label, entityType, testid }) {
  if (!entity) return <div className="text-sm text-muted-foreground">Record unavailable</div>;
  const rows =
    entityType === 'business'
      ? [
          ['Legal name', entity.legal_name || '—'],
          ['DBA', (entity.dba_names || []).join(', ') || '—'],
          ['Address', addressLine(entity.address)],
          ['EIN', entity.ein_last4 ? `•••• ${entity.ein_last4}` : '—'],
          ['Domain', entity.domain || '—'],
          ['Email', (entity.emails || []).join(', ') || '—'],
          ['Phone', (entity.phones || []).join(', ') || '—'],
          ['Merchant group', entity.merchant_group_id ? 'Linked' : '—'],
          ['Source records', String((entity.source_records || []).length)],
        ]
      : [
          ['Name', entity.full_name || '—'],
          ['Title', entity.title || '—'],
          ['Emails', (entity.emails || []).map((e) => e.value || e.normalized).join(', ') || '—'],
          ['Phones', (entity.phones || []).map((p) => p.value || p.normalized).join(', ') || '—'],
          ['Businesses', String((entity.business_ids || []).length)],
          ['Source records', String((entity.source_records || []).length)],
        ];

  return (
    <div className="flex-1 rounded-lg border border-border bg-card/40 p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">{label}</p>
      <dl className="space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[110px_1fr] gap-2">
            <dt className="text-xs text-muted-foreground">{k}</dt>
            <dd className="text-sm break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function CandidateRow({ candidate, onDecision, selected, onToggleSelect }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    setBusy(true);
    try {
      await api.decideIdentityCandidate(candidate.id, action, notes);
      toast.success(action === 'approve' ? 'Records merged' : 'Kept as separate businesses');
      onDecision();
    } catch (e) {
      toast.error(e?.message || 'Could not save that decision');
    } finally {
      setBusy(false);
    }
  };

  const band = BAND_STYLES[candidate.confidence_band] || BAND_STYLES.weak;

  return (
    <div
      className="rounded-xl border border-border bg-background p-5 space-y-4"
      data-testid={`identity-candidate-${candidate.id}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(candidate.id)}
            className="h-4 w-4 accent-primary"
            data-testid={`candidate-checkbox-${candidate.id}`}
          />
          Select
        </label>
        <Badge variant="outline" className={band} data-testid="candidate-confidence-badge">
          {Math.round((candidate.confidence || 0) * 100)}% · {(candidate.confidence_band || '').replace(/_/g, ' ')}
        </Badge>
        <Badge variant="outline">{candidate.entity_type}</Badge>
        {candidate.source_type && (
          <span className="text-xs text-muted-foreground">
            arrived via {candidate.source_type.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      <ul className="flex flex-wrap gap-2">
        {(candidate.signals || []).map((s) => (
          <li key={s} className="text-xs rounded-full border border-border px-2.5 py-1 text-muted-foreground">
            {SIGNAL_LABELS[s] || s}
          </li>
        ))}
      </ul>

      <div className="flex flex-col lg:flex-row gap-4">
        <IdentityCard
          entity={candidate.new_entity}
          entityType={candidate.entity_type}
          label="Incoming record"
          testid={`candidate-new-${candidate.id}`}
        />
        <div className="hidden lg:flex items-center justify-center px-2">
          <ArrowLeftRight className="h-5 w-5 text-muted-foreground" />
        </div>
        <IdentityCard
          entity={candidate.existing_entity}
          entityType={candidate.entity_type}
          label="Existing record"
          testid={`candidate-existing-${candidate.id}`}
        />
      </div>

      <Textarea
        placeholder="Why are these the same (or not)? Optional, kept in the audit trail."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="text-sm"
        data-testid={`candidate-notes-${candidate.id}`}
      />

      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => decide('approve')}
          disabled={busy}
          data-testid={`candidate-approve-${candidate.id}`}
        >
          <GitMerge className="h-4 w-4 mr-2" />
          Same business — merge
        </Button>
        <Button
          variant="outline"
          onClick={() => decide('reject')}
          disabled={busy}
          data-testid={`candidate-reject-${candidate.id}`}
        >
          Different businesses — keep separate
        </Button>
      </div>
    </div>
  );
}

export default function IdentityReviewPage() {
  const [candidates, setCandidates] = useState([]);
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [selected, setSelected] = useState([]);
  const [minConfidence, setMinConfidence] = useState(0);
  const [sortBy, setSortBy] = useState('confidence');

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.listIdentityCandidates(200, sortBy),
        api.listIdentityMerges(100),
      ]);
      setCandidates(c || []);
      setMerges(m || []);
    } catch (e) {
      toast.error('Could not load the duplicate review queue');
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    load();
  }, [load]);

  const bulkDecide = async (decision) => {
    if (actionable.length === 0) return;
    try {
      const res = await api.bulkDecideIdentityCandidates(actionable, decision);
      toast.success(
        decision === 'reject'
          ? `${res.updated} kept as separate businesses`
          : `${res.updated} deferred for later`
      );
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.message || 'Could not apply that to the selected records');
    }
  };

  const undo = async (id) => {
    try {
      await api.reverseIdentityMerge(id);
      toast.success('Merge reversed');
      load();
    } catch (e) {
      toast.error(e?.message || 'Could not reverse that merge');
    }
  };

  const visibleCandidates = candidates.filter((c) => (c.confidence || 0) >= minConfidence);
  // Never act on rows the filter is hiding.
  const actionable = selected.filter((id) => visibleCandidates.some((c) => c.id === id));
  const selectFirst25 = () => setSelected(visibleCandidates.slice(0, 25).map((c) => c.id));

  return (
    <div className="space-y-8" data-testid="identity-review-page">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">Duplicate Review</h1>
        </div>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
          Records matched on an EIN or a processor MID are linked automatically. Anything matched on a
          name, DBA or address waits here for your decision — nothing is ever merged silently.
        </p>
      </header>

      <div className="flex gap-2">
        <Button
          variant={tab === 'pending' ? 'default' : 'outline'}
          onClick={() => setTab('pending')}
          data-testid="identity-tab-pending"
        >
          Pending ({visibleCandidates.length})
        </Button>
        <Button
          variant={tab === 'history' ? 'default' : 'outline'}
          onClick={() => setTab('history')}
          data-testid="identity-tab-history"
        >
          <History className="h-4 w-4 mr-2" />
          Merge history ({merges.length})
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground" data-testid="identity-review-loading">Loading…</p>
      ) : tab === 'pending' ? (
        candidates.length === 0 ? (
          <div
            className="rounded-xl border border-dashed border-border p-12 text-center"
            data-testid="identity-review-empty"
          >
            <p className="text-base font-medium">No duplicates waiting</p>
            <p className="text-sm text-muted-foreground mt-1">
              Every incoming record resolved cleanly to a single business.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                data-testid="sort-by-risk"
              >
                <option value="confidence">Highest risk first</option>
                <option value="newest">Newest first</option>
              </select>
              <select
                value={minConfidence}
                onChange={(e) => setMinConfidence(Number(e.target.value))}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                data-testid="confidence-filter"
              >
                <option value={0}>All confidences</option>
                <option value={0.5}>50% and above</option>
                <option value={0.7}>70% and above</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={selectFirst25}
                data-testid="select-25-btn"
              >
                Select 25
              </Button>
              <span className="text-sm text-muted-foreground ml-auto" data-testid="candidate-selected-count">
                {actionable.length} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={actionable.length === 0}
                onClick={() => bulkDecide('reject')}
                data-testid="bulk-keep-separate-btn"
              >
                Keep separate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={actionable.length === 0}
                onClick={() => bulkDecide('defer')}
                data-testid="bulk-defer-btn"
              >
                Defer
              </Button>
              <span className="text-xs text-muted-foreground basis-full">
                Merging two businesses is reviewed one record at a time — bulk actions can only keep
                records separate or defer them.
              </span>
            </div>
            {visibleCandidates.length === 0 && (
              <div
                className="rounded-xl border border-dashed border-border p-10 text-center"
                data-testid="identity-review-filtered-empty"
              >
                <p className="text-base font-medium">Nothing matches this confidence filter</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {candidates.length} record{candidates.length === 1 ? '' : 's'} are waiting at a
                  lower confidence. Choose “All confidences” to see them.
                </p>
              </div>
            )}
            <div className="space-y-5" data-testid="identity-candidate-list">
              {visibleCandidates.map((c) => (
                <CandidateRow
                  key={c.id}
                  candidate={c}
                  onDecision={load}
                  selected={selected.includes(c.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
          </>
        )
      ) : (
        <div className="space-y-3" data-testid="identity-merge-history">
          {merges.length === 0 && (
            <p className="text-sm text-muted-foreground">No merges have been performed yet.</p>
          )}
          {merges.map((m) => (
            <div
              key={m.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-4"
              data-testid={`identity-merge-${m.id}`}
            >
              <div className="space-y-1">
                <p className="text-sm">
                  {(m.signals || []).map((s) => SIGNAL_LABELS[s] || s).join(' · ') || 'Merged'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(m.performed_at).toLocaleString()} ·{' '}
                  {Object.keys(m.moved_references || {}).length} reference group(s) repointed
                  {m.reversed_at ? ' · reversed' : ''}
                </p>
              </div>
              {!m.reversed_at && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => undo(m.id)}
                  data-testid={`identity-merge-undo-${m.id}`}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Undo
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
